import * as React from "react"

type Theme = "dark" | "light" | "system"
type ResolvedTheme = "dark" | "light"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  disableTransitionOnChange?: boolean
  /**
   * A mode the document paints REGARDLESS of the stored preference — for a
   * region of the app that belongs to no one yet (a sign-in screen, a picker
   * that precedes the space whose preference would apply). `theme` and
   * `setTheme` keep describing and writing the stored preference underneath,
   * so a control that edits it still reads the user's choice; only what the
   * DOM wears — and `resolvedTheme` with it — follows the override. Clear it
   * and the stored preference paints again.
   */
  forcedTheme?: Theme | undefined
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  /**
   * The concrete mode the DOM is actually in — `theme` with "system" already
   * resolved against the media query. This provider computes it anyway to pick
   * the class it puts on <html>, so consumers that need the mode read it from
   * here rather than starting a second MutationObserver on the same attribute.
   */
  resolvedTheme: ResolvedTheme
}

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)"
const THEME_VALUES: Theme[] = ["dark", "light", "system"]

const ThemeProviderContext = React.createContext<
  ThemeProviderState | undefined
>(undefined)

function isTheme(value: string | null): value is Theme {
  if (value === null) {
    return false
  }

  return THEME_VALUES.includes(value as Theme)
}

function getSystemTheme(): ResolvedTheme {
  if (window.matchMedia(COLOR_SCHEME_QUERY).matches) {
    return "dark"
  }

  return "light"
}

function disableTransitionsTemporarily() {
  const style = document.createElement("style")
  style.appendChild(
    document.createTextNode(
      "*,*::before,*::after{-webkit-transition:none!important;transition:none!important}"
    )
  )
  document.head.appendChild(style)

  return () => {
    window.getComputedStyle(document.body)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        style.remove()
      })
    })
  }
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  disableTransitionOnChange = true,
  forcedTheme,
  ...props
}: ThemeProviderProps) {
  // Both initializers run during server rendering, where localStorage and
  // matchMedia do not exist. The server renders the default; the mount effect
  // below applies the stored/system theme on the client.
  const [theme, setThemeState] = React.useState<Theme>(() => {
    if (typeof window === "undefined") {
      return defaultTheme
    }

    const storedTheme = localStorage.getItem(storageKey)
    if (isTheme(storedTheme)) {
      return storedTheme
    }

    return defaultTheme
  })

  // What the document wears: the override when one is set, else the stored
  // preference.
  const painted = forcedTheme ?? theme

  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(
    () => {
      if (painted !== "system") {
        return painted
      }

      return typeof window === "undefined" ? "light" : getSystemTheme()
    }
  )

  const setTheme = React.useCallback(
    (nextTheme: Theme) => {
      localStorage.setItem(storageKey, nextTheme)
      setThemeState(nextTheme)
    },
    [storageKey]
  )

  const applyTheme = React.useCallback(
    (nextTheme: Theme) => {
      const root = document.documentElement
      const resolved = nextTheme === "system" ? getSystemTheme() : nextTheme
      const restoreTransitions = disableTransitionOnChange
        ? disableTransitionsTemporarily()
        : null

      root.classList.remove("light", "dark")
      root.classList.add(resolved)
      setResolvedTheme(resolved)

      if (restoreTransitions) {
        restoreTransitions()
      }
    },
    [disableTransitionOnChange]
  )

  React.useEffect(() => {
    applyTheme(painted)

    if (painted !== "system") {
      return undefined
    }

    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY)
    const handleChange = () => {
      applyTheme("system")
    }

    mediaQuery.addEventListener("change", handleChange)

    return () => {
      mediaQuery.removeEventListener("change", handleChange)
    }
  }, [painted, applyTheme])

  React.useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return
      }

      if (event.key !== storageKey) {
        return
      }

      if (isTheme(event.newValue)) {
        setThemeState(event.newValue)
        return
      }

      setThemeState(defaultTheme)
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [defaultTheme, storageKey])

  const value = React.useMemo(
    () => ({
      theme,
      setTheme,
      resolvedTheme,
    }),
    [theme, setTheme, resolvedTheme]
  )

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = React.useContext(ThemeProviderContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}

/**
 * The concrete mode, for a component that only needs to KNOW it — a label
 * that names a fill by how it renders, not a control that sets the theme.
 * Outside a provider it reads the class the provider would have put on
 * <html>, so a bare render still answers.
 */
export const useThemeMode = (): ResolvedTheme => {
  const context = React.useContext(ThemeProviderContext)
  if (context !== undefined) return context.resolvedTheme
  return typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
    ? "dark"
    : "light"
}
